import { useState, useCallback } from 'react';
import { ShoppingCart, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BoampSearchFilters } from '../components/search/BoampSearchFilters';
import { TedSearchFilters } from '../components/search/TedSearchFilters';
import { SearchResults } from '../components/search/SearchResults';
import { ImportCart } from '../components/search/ImportCart';
import { useBoampSearch, useExcludeOpportunity } from '../hooks/useBoampSearch';
import { useTedSearch } from '../hooks/useTedSearch';
import { useImportCartStore } from '../stores/import-cart.store';
import {
  BoampSearchParams,
  TedSearchParams,
  ExternalOpportunity,
  ExternalSource,
} from '../types/opportunity.types';
import { toast } from 'sonner';

export function OpportunitySearchPage() {
  const [activeSource, setActiveSource] = useState<ExternalSource>('boamp');

  // State BOAMP
  const [boampParams, setBoampParams] = useState<BoampSearchParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'dateparution',
    sortOrder: 'desc',
  });
  const [boampSearched, setBoampSearched] = useState(false);

  // State TED
  const [tedParams, setTedParams] = useState<TedSearchParams>({
    page: 1,
    pageSize: 20,
    sortField: 'PD',
    reverseOrder: false,
  });
  const [tedSearched, setTedSearched] = useState(false);

  // Exclusion
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [opportunityToExclude, setOpportunityToExclude] =
    useState<ExternalOpportunity | null>(null);
  const [excludeReason, setExcludeReason] = useState('');

  const { items, openCart } = useImportCartStore();

  // Queries
  const boampQuery = useBoampSearch(boampParams, boampSearched && activeSource === 'boamp');
  const tedQuery = useTedSearch(tedParams, tedSearched && activeSource === 'ted');

  const excludeMutation = useExcludeOpportunity();

  // Dériver les données actuelles selon la source active
  const currentData = activeSource === 'boamp' ? boampQuery.data : tedQuery.data;
  const isLoading = activeSource === 'boamp' ? boampQuery.isLoading : tedQuery.isLoading;
  const isError = activeSource === 'boamp' ? boampQuery.isError : tedQuery.isError;
  const refetch = activeSource === 'boamp' ? boampQuery.refetch : tedQuery.refetch;

  const handleBoampSearch = useCallback((params: BoampSearchParams) => {
    setBoampParams(params);
    setBoampSearched(true);
  }, []);

  const handleTedSearch = useCallback((params: TedSearchParams) => {
    setTedParams(params);
    setTedSearched(true);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (activeSource === 'boamp') {
        setBoampParams((prev) => ({ ...prev, page }));
      } else {
        setTedParams((prev) => ({ ...prev, page }));
      }
    },
    [activeSource],
  );

  const handleExcludeClick = useCallback((opportunity: ExternalOpportunity) => {
    setOpportunityToExclude(opportunity);
    setExcludeReason('');
    setExcludeDialogOpen(true);
  }, []);

  const handleExcludeConfirm = async () => {
    if (!opportunityToExclude) return;

    try {
      await excludeMutation.mutateAsync({
        externalId: opportunityToExclude.externalId,
        source: opportunityToExclude.source,
        reason: excludeReason || undefined,
      });
      toast.success('Opportunité exclue des futures recherches');
      setExcludeDialogOpen(false);
      setOpportunityToExclude(null);
      refetch();
    } catch (error) {
      toast.error("Erreur lors de l'exclusion");
    }
  };

  const handleImportSuccess = () => {
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Search className="w-6 h-6 text-copper" />
            Recherche d'Appels d'Offres
          </h1>
          <p className="text-muted-foreground mt-1">
            {activeSource === 'boamp'
              ? "Recherchez et importez des appels d'offres depuis BOAMP (France)"
              : "Recherchez et importez des appels d'offres depuis TED (Journal officiel de l'UE)"}
          </p>
        </div>

        {/* Bouton panier */}
        <Button
          variant="outline"
          onClick={openCart}
          className="relative"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Panier
          {items.length > 0 && (
            <Badge
              className="absolute -top-2 -right-2 bg-copper hover:bg-copper"
              variant="default"
            >
              {items.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Onglets source */}
      <Tabs
        value={activeSource}
        onValueChange={(v) => setActiveSource(v as ExternalSource)}
      >
        <TabsList>
          <TabsTrigger value="boamp">BOAMP (France)</TabsTrigger>
          <TabsTrigger value="ted">TED (Europe)</TabsTrigger>
        </TabsList>

        <TabsContent value="boamp" className="mt-4">
          <BoampSearchFilters onSearch={handleBoampSearch} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="ted" className="mt-4">
          <TedSearchFilters onSearch={handleTedSearch} isLoading={isLoading} />
        </TabsContent>
      </Tabs>

      {/* Résultats — partagés entre les deux sources */}
      <SearchResults
        data={currentData}
        isLoading={isLoading}
        isError={isError}
        onPageChange={handlePageChange}
        onExclude={handleExcludeClick}
      />

      {/* Panier slide-in */}
      <ImportCart onImportSuccess={handleImportSuccess} />

      {/* Dialog d'exclusion */}
      <Dialog open={excludeDialogOpen} onOpenChange={setExcludeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exclure cette opportunité</DialogTitle>
            <DialogDescription>
              L'opportunité sera exclue des futures recherches pour votre
              organisation. Cette action est réversible.
            </DialogDescription>
          </DialogHeader>

          {opportunityToExclude && (
            <div className="py-4">
              <p className="text-sm font-medium mb-4 line-clamp-2">
                {opportunityToExclude.title}
              </p>
              <div className="space-y-2">
                <Label htmlFor="reason">Raison de l'exclusion (optionnel)</Label>
                <Input
                  id="reason"
                  placeholder="Ex: Hors scope géographique, Marché trop petit..."
                  value={excludeReason}
                  onChange={(e) => setExcludeReason(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExcludeDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleExcludeConfirm}
              disabled={excludeMutation.isPending}
            >
              Exclure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
